"use client";

import { useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { usePlatform } from "@/hooks/use-platform";
import type { MenuItem, MenuSection } from "@/config/menu";

/**
 * Filters menu items and sections based on user permissions and subscription features.
 * No plan-expiry gate on menu items — inner pages handle "no access" states themselves.
 */
export function useMenuPermissions() {
  const { user } = useAuth();
  const { canManageTeamMembers, knowledgeBaseEnabled } = usePermissions();
  const { isMobile } = usePlatform();

  const features: Record<string, Record<string, boolean> | undefined> =
    user?.subscription?.features ?? {};
  const canAccessDevApi = !!user?.permission?.developer?.accessKeys;

  const filterItem = useMemo(() => {
    return (item: Pick<MenuItem, "requiresPermission" | "requiresFeature" | "lockedFeature" | "isMobileOnly">): boolean => {
      // Permission checks
      if (item.requiresPermission === "team") {
        if (!canManageTeamMembers) return false;
      }

      if (item.requiresPermission === "developers") {
        if (!canAccessDevApi) return false;
      }

      if (item.requiresPermission === "knowledgeBase") {
        if (!knowledgeBaseEnabled) return false;
      }

      // Feature checks (dot notation: "category.feature")
      if (item.requiresFeature) {
        const [category, feature] = item.requiresFeature.split(".");
        if (!features[category]?.[feature]) return false;
      }

      // Mobile-only items hidden on desktop
      if (item.isMobileOnly && !isMobile) return false;

      return true;
    };
  }, [canManageTeamMembers, canAccessDevApi, knowledgeBaseEnabled, features, isMobile]);

  const filterSection = useMemo(() => {
    return (section: MenuSection): MenuSection | null => {
      // Check section-level permissions
      if (section.requiresPermission || section.requiresFeature) {
        if (!filterItem(section)) return null;
      }

      // Filter children
      const filteredItems = section.items.filter(filterItem);

      // Hide section if no children visible
      if (filteredItems.length === 0) return null;

      return { ...section, items: filteredItems };
    };
  }, [filterItem]);

  const isItemLocked = useMemo(() => {
    return (item: Pick<MenuItem, "requiresPermission" | "requiresFeature" | "lockedFeature" | "isMobileOnly">): boolean => {
      if (!item.lockedFeature) return false;
      const [category, feature] = item.lockedFeature.split(".");
      return !features[category]?.[feature];
    };
  }, [features]);

  return { filterItem, filterSection, isItemLocked };
}
